import { config } from '../config/environment';
import { readSseStream } from '../utils/sseStreamReader';

export interface ChatMessageDTO {
  role: 'USER' | 'ASSISTANT';
  content: string;
  createdAt: string;
}

export interface ChatConversationDTO {
  conversationId: number;
  messages: ChatMessageDTO[];
}

export interface ChatStreamHandlers {
  onChunk: (text: string) => void;
  /** options is set only for voice-originated messages when the answer involves a choice (FR-016). */
  onDone: (data: { messageId: number; createdAt: string; options?: string[] }) => void;
  onError: (message: string) => void;
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export async function getChatConversation(
  role: 'tutor' | 'student',
  courseId: number,
  nodeId: number
): Promise<ChatConversationDTO> {
  const response = await fetch(
    `${config.coreApiUrl}/api/${role}/courses/${courseId}/nodes/${nodeId}/ai-chat`,
    { headers: authHeaders() }
  );
  const body = await response.json();
  return body.data;
}

/** Sends a chat message and streams the AI's response. Resolves once the stream ends (done or error). */
export async function sendChatMessage(
  role: 'tutor' | 'student',
  courseId: number,
  nodeId: number,
  message: string,
  handlers: ChatStreamHandlers,
  inputMode?: 'VOICE'
): Promise<void> {
  const response = await fetch(
    `${config.coreApiUrl}/api/${role}/courses/${courseId}/nodes/${nodeId}/ai-chat/messages`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ message, inputMode }),
    }
  );

  await readSseStream(response, {
    onEvent: ({ event, data }) => {
      if (event === 'chunk') {
        handlers.onChunk(data.text);
      } else if (event === 'done') {
        handlers.onDone(data);
      } else if (event === 'error') {
        handlers.onError(data.message || 'Generation failed — please try again.');
      }
    },
    onError: () => handlers.onError('Connection lost — please try again.'),
  });
}
